/**
 * Notebook cover colors. A collection's `color` ("#rrggbb") is stored server-side
 * and shared with mobile; this turns any hex into readable card colors.
 */

/** Preset covers — mirrors PRESET_COLORS (backend/services/collections.py) and
 *  COLLECTION_COVERS (mobile/src/theme/tokens.ts), in order. */
export const COVER_PRESETS = [
  { name: 'Charcoal', hex: '#2b2a28' },
  { name: 'Violet', hex: '#5b3fd0' },
  { name: 'Forest', hex: '#3e5c50' },
  { name: 'Rust', hex: '#9a4a2c' },
  { name: 'Kraft', hex: '#d9cfbd' },
  { name: 'Navy', hex: '#2c4a6e' },
  { name: 'Plum', hex: '#7a3b69' },
  { name: 'Mustard', hex: '#c99a2e' },
] as const;

/** By-position covers for a server that predates stored colors — the same five
 *  mobile falls back to (COLLECTION_CARD_COLORS), so both apps agree. */
const LEGACY_COVERS = COVER_PRESETS.slice(0, 5).map((p) => p.hex);

const INK = '#1c1b19';
const WHITE = '#ffffff';

export interface CoverStyle {
  bg: string;
  text: string;
  muted: string;
  subtle: string;
  border: string;
}

export function isHexColor(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

/** Dark covers need light text. */
export function isDark(hex: string): boolean {
  const c = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(c.slice(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.6;
}

/** Card colors for a cover: white text on dark covers, ink on light ones. */
export function coverStyle(hex: string): CoverStyle {
  if (isDark(hex)) {
    return {
      bg: hex,
      text: WHITE,
      muted: 'rgba(255,255,255,0.6)',
      subtle: 'rgba(255,255,255,0.8)',
      border: 'rgba(255,255,255,0.2)',
    };
  }
  return {
    bg: hex,
    text: INK,
    muted: 'rgba(28,27,25,0.55)',
    subtle: 'rgba(28,27,25,0.75)',
    border: 'rgba(28,27,25,0.12)',
  };
}

/** A collection's cover: its stored color, else the legacy position-based one. */
export function coverFor(collection: { color: string | null }, index: number): string {
  return collection.color ?? LEGACY_COVERS[index % LEGACY_COVERS.length];
}
